/**
 * The per-tick checksum (ARCHITECTURE.md §8 move 4; WORKBOARD R11 "a per-tick checksum"): one 32-bit
 * hash over everything that can decide an outcome, cheap enough to take every tick. It is what the
 * surrogate's agreement test, catch-up re-entry, snapshot → restore and a real-time determinism bug
 * hunt all compare.
 *
 * Unlike the per-turn checksum (network/protocol.ts, which the turn goldens are recorded with and so
 * cannot change) it is **exact** — every number by its full float bits — and **generic**: it walks the
 * whole state with sorted keys, so a field added tomorrow is covered without anyone remembering to.
 * An optional field that is undefined hashes as absent.
 *
 * Left out, because they decide nothing: `shotRecords` (analysis), `terrainEdits` (a log of the craters,
 * whose heightmaps are hashed), `propEvents` (a log of the props' blows, whose health and fall are hashed) and
 * `dressedAreas` (E5.4: where the dressing laid its stands, written once at setup; the trees it shapes are hashed). The input log lives on the Match (`inputLog`), not in the state.
 *
 * Large arrays — typed (heightmaps, base positions, fog snapshots) and plain (fog's revealed flags) —
 * are hashed in full once per array and cached by identity (core/contentHash.ts): the sim replaces them
 * when they change (craters; fog updates copy), and the one in-place writer, fog's revealAtPoint,
 * invalidates the arrays it touches. Each tick adds a fresh sample of 32 words spread across each large
 * array as a backstop. Walking every element instead cost ~5 ms a tick on a megaplanet with a dozen
 * players' fog.
 *
 * Not covered yet: bot memory held in closures (the action bot's brains). R11 moves it into state;
 * until then `Match.tickChecksum` folds in what the Match itself holds (tick, intents, heard shots).
 */
import type { GameState } from '../types';
import { mix, typedArrayHash, plainArrayHash } from '../core/contentHash';

const EXCLUDED = new Set(['shotRecords', 'terrainEdits', 'propEvents', 'dressedAreas']);   // logs and records: they decide nothing, what they describe or shape does
/** Words sampled fresh from each large array every tick (a fixed cost per array). */
const SAMPLES = 32;
/** Plain arrays longer than this are hashed once and cached by identity, like typed arrays. */
const LARGE = 256;
const f64 = new Float64Array(1);
const f64Words = new Uint32Array(f64.buffer);

/** Key names repeat every tick: hash each once. */
const keyHashes = new Map<string, number>();
function keyHash(k: string): number {
  let x = keyHashes.get(k);
  if (x === undefined) {
    x = 0x811c9dc5; for (let i = 0; i < k.length; i++) x = mix(x, k.charCodeAt(i));
    if (keyHashes.size >= 8192) keyHashes.clear();   // bounded over a long session; the vocabulary refills it
    keyHashes.set(k, x);
  }
  return x;
}

/** An object's defined keys, sorted (sorting skipped when already in order — most shapes repeat). */
function sortedKeys(o: Record<string, unknown>): string[] {
  const keys: string[] = [];
  let sorted = true;
  for (const k in o) {
    if (o[k] === undefined || !Object.prototype.hasOwnProperty.call(o, k)) continue;
    if (keys.length > 0 && keys[keys.length - 1] > k) sorted = false;
    keys.push(k);
  }
  return sorted ? keys : keys.sort();
}

function mixNumber(h: number, v: number): number {
  f64[0] = v;
  return mix(mix(h, f64Words[0]), f64Words[1]);
}

function hashTyped(h: number, a: ArrayBufferView): number {
  const { full, words } = typedArrayHash(a);
  h = mix(mix(mix(h, 6), full), words.length);
  const stride = Math.max(1, Math.floor(words.length / SAMPLES)) | 1;   // odd: walks the x / y / z interleave
  for (let i = 0; i < words.length; i += stride) h = mix(h, words[i]);
  return h;
}

function hashPlain(h: number, v: unknown[]): number {
  if (v.length <= LARGE) { h = mix(mix(h, 5), v.length); for (const x of v) h = hashValue(h, x); return h; }
  const full = plainArrayHash(v, arr => { let x = 0x811c9dc5; for (const e of arr) x = hashValue(x, e); return x; });
  h = mix(mix(mix(h, 10), full), v.length);
  const stride = Math.max(1, Math.floor(v.length / SAMPLES)) | 1;
  for (let i = 0; i < v.length; i += stride) h = hashValue(h, v[i]);
  return h;
}

function hashValue(h: number, v: unknown): number {
  if (v === null) return mix(h, 1);
  switch (typeof v) {
    case 'boolean': return mix(mix(h, 2), v ? 1 : 0);
    case 'number': return mixNumber(mix(h, 3), v);
    case 'string': return mix(mix(mix(h, 4), v.length), keyHash(v));   // names, keys, labels: a small vocabulary, hashed once each
    case 'object': break;
    default: return h;   // undefined, functions: absent
  }
  if (ArrayBuffer.isView(v)) return hashTyped(h, v);
  if (Array.isArray(v)) return hashPlain(h, v);
  if (v instanceof Map) {
    const entries = [...v.entries()].sort((a, b) => (String(a[0]) < String(b[0]) ? -1 : String(a[0]) > String(b[0]) ? 1 : 0));
    h = mix(mix(h, 7), entries.length);
    for (const [k, x] of entries) h = hashValue(hashValue(h, k), x);
    return h;
  }
  if (v instanceof Set) return hashValue(mix(h, 9), [...v].sort());
  const o = v as Record<string, unknown>;
  const keys = sortedKeys(o);
  h = mix(mix(h, 8), keys.length);
  for (const k of keys) h = hashValue(mix(h, keyHash(k)), o[k]);
  return h;
}

/** The exact checksum of everything in `state` that can decide an outcome, plus any `extra` decision state the caller holds. */
export function tickChecksum(state: GameState, extra?: unknown): number {
  let h = 0x811c9dc5;
  const s = state as unknown as Record<string, unknown>;
  for (const k of sortedKeys(s)) if (!EXCLUDED.has(k)) h = hashValue(mix(h, keyHash(k)), s[k]);
  if (extra !== undefined) h = hashValue(mix(h, 0x5eed), extra);
  return h >>> 0;
}
